import React from 'react';
import { cn } from '../../utils';

export interface LoadingSkeletonProps {
  /**
   * Type of skeleton to display
   */
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  /**
   * Width of the skeleton (CSS value)
   */
  width?: string | number;
  /**
   * Height of the skeleton (CSS value)
   */
  height?: string | number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Number of skeleton lines (for text variant)
   * @default 1
   */
  lines?: number;
  /**
   * Whether to animate the skeleton
   * @default true
   */
  animate?: boolean;
}

/**
 * LoadingSkeleton provides placeholder UI while content loads
 * Based on feedback from @orchard9ai/create-vite-vike-tauri generator team
 *
 * @example
 * ```tsx
 * // Text skeleton
 * <LoadingSkeleton variant="text" lines={3} />
 *
 * // Avatar skeleton
 * <LoadingSkeleton variant="circular" width={48} height={48} />
 *
 * // Card skeleton
 * <LoadingSkeleton variant="card" height={200} />
 * ```
 */
export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className,
  lines = 1,
  animate = true,
}) => {
  const baseClasses = cn('bg-base-300', animate && 'animate-pulse', className);

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  // Text skeleton with multiple lines
  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(
              baseClasses,
              'h-4 rounded',
              // Make last line shorter for natural text appearance
              index === lines - 1 && 'w-3/4'
            )}
            style={{ width: index === lines - 1 ? '75%' : width }}
          />
        ))}
      </div>
    );
  }

  // Single element skeletons
  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded',
    card: 'rounded-lg',
  };

  // Card skeleton with predefined structure
  if (variant === 'card') {
    return (
      <div className={cn(baseClasses, 'p-4 space-y-3', variantClasses[variant])} style={style}>
        {/* Card header skeleton */}
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-base-200 rounded-full animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-base-200 rounded w-3/4 animate-pulse" />
            <div className="h-3 bg-base-200 rounded w-1/2 animate-pulse" />
          </div>
        </div>
        {/* Card body skeleton */}
        <div className="space-y-2">
          <div className="h-3 bg-base-200 rounded animate-pulse" />
          <div className="h-3 bg-base-200 rounded animate-pulse" />
          <div className="h-3 bg-base-200 rounded w-5/6 animate-pulse" />
        </div>
      </div>
    );
  }

  // Default single element skeleton
  return (
    <div
      className={cn(baseClasses, variantClasses[variant])}
      style={style}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};
